import { useColorMode, useEventListener } from '@vueuse/core'
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'

import type { IConfig, IMe } from '@/types/User'

export const useAppStore = defineStore('app', () => {
    const windowWidth  = ref(window.innerWidth)
    const loaderActive = ref(0)
    const me           = ref<IMe | null>(null)
    const config       = ref<IConfig | null>(null)
    const meLoaded     = ref(false)
    /** Set after an email sign-in so the passkey banner is offered (spec §3.4). */
    const justSignedInWithEmail = ref(false)

    // Wraps the SAME state @nuxt/ui's vue plugin already manages via useDark() — two modes only.
    const colorMode = useColorMode({ storageKey: 'vueuse-color-scheme' })

    useEventListener(window, 'resize', () => { windowWidth.value = window.innerWidth })

    const isLoggedIn     = () => !!me.value
    const isLoaderActive = () => loaderActive.value > 0
    const isMobile       = () => windowWidth.value < 1024
    const isAdmin        = computed(() => me.value?.system_role === 'admin')
    const isTest         = computed(() => config.value?.furs_env === 'test')

    // Kept for the shell templates, which read `userData`.
    const userData = computed(() => me.value
        ? { id: me.value.id, name: me.value.full_name || me.value.email, username: me.value.email }
        : undefined)

    const setMe = (value: IMe | null) => {
        me.value = value
        meLoaded.value = true
    }

    const setLogout = () => {
        me.value = null
        meLoaded.value = true
    }

    const startLoading = () => loaderActive.value++
    const stopLoading  = () => loaderActive.value = Math.max(0, loaderActive.value - 1)

    return {
        me, config, meLoaded, userData, loaderActive, colorMode, justSignedInWithEmail,
        isAdmin, isTest,
        isLoggedIn, isLoaderActive, isMobile,
        setMe, setLogout, startLoading, stopLoading,
    }
})
